import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    SerializedError,
} from '@reduxjs/toolkit';
import { urlStampsAll, urlStampsDelete, urlStampsImage, urlStampsSave } from '../../Constants';
import { IAppReduxStore, IAction, SlicePropsWithData } from '../store';
import { downloadDocumentsStream } from '../../util/FileUtils';
import { GlobalVariables } from '../../GlobalVariables';
import { filesReducers, IFileInfoState, initialFileState } from '../general/files';
import { apiFetch } from '../../util/ProjectUtils';

const mapStamps = (payload: IUserStamp[]) =>
    Object.entries(
        payload.reduce((acc, obj) => {
            const category = obj.category || 'Unknown';
            if (!acc[category]) {
                acc[category] = [];
            }
            acc[category].push(obj);
            return acc;
        }, {}),
    ).map(([category, stamps]: [string, IUserStamp[]]) => ({
        category,
        stamps,
    }));

const mapCategories = (payload: IUserStamp[]) =>
    [...new Set(payload.map(item => item.category))].filter(category => category !== null);

/**
 * Initial slice state value.
 */
const initialState: IUserStampsState = {
    data: null,
    isCreateStampDialogOpen: false,
    isStampSaved: false,
    loading: false,
    error: null,
    stamps: [],
    categories: [],
    stampsResponse: [],
    ...initialFileState,
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {
    openCreateStampDialog(state: IUserStampsState) {
        state.isCreateStampDialogOpen = true;
    },
    closeCreateStampDialog(state: IUserStampsState) {
        state.isCreateStampDialogOpen = false;
        state.isStampSaved = false;

        state.fileInfo = null;
    },

    ...filesReducers,
};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Fetches stamps from the backend.
     */
    getStamps: async (): Promise<IUserStamp[]> => {
        try {
            const request: RequestInit = {
                method: 'GET',
            };

            const response = await apiFetch(urlStampsAll, request);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    getStampImage: async (config: {
        stampId: string;
        stampName: string;
        fileExtension: string;
    }): Promise<void> => {
        try {
            const request: RequestInit = {
                method: 'GET',
                headers: {
                    ['stampKey']: config.stampId,
                },
            };

            const response = await apiFetch(urlStampsImage, request);

            if (response.ok) {
                downloadDocumentsStream(
                    await response.blob(),
                    `${config.stampName}${config.fileExtension}`,
                );
                return Promise.resolve();
            }

            return Promise.reject();
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    saveStamp: async (config: { name: string; category: string }, thunkApi): Promise<void> => {
        const { fileInfo } = (thunkApi.getState() as IAppReduxStore).userStamps;
        try {
            const stampInfo = JSON.stringify({
                name: config.name,
                category: config.category,
                id: Date.now().toString(36) + Math.random().toString(36).slice(2),
            });
            const uploadStampData = new FormData();
            uploadStampData.append('stampInfo', stampInfo);
            uploadStampData.append('stamp', GlobalVariables.getFileById(fileInfo.id));

            const request: RequestInit = {
                method: 'POST',
                body: uploadStampData,
            };

            const response = await apiFetch(urlStampsSave, request);

            return response.ok
                ? Promise.resolve()
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    deleteStamp: async (config: { stampId: string }): Promise<string> => {
        try {
            const request: RequestInit = {
                method: 'DELETE',
                headers: {
                    ['stampKey']: config.stampId,
                },
            };

            const response = await apiFetch(urlStampsDelete, request);

            return response.ok ? config.stampId : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IUserStampsState>) => {
        /**
         * Reducers for the 'getAllStamps' thunk.
         */
        builder.addCase(getAllStamps.pending, state => {
            state.loading = true;
        });
        builder.addCase(getAllStamps.fulfilled, (state, action) => {
            state.stampsResponse = action.payload;
            state.stamps = mapStamps(action.payload);
            state.categories = mapCategories(action.payload);
            state.loading = false;
        });
        builder.addCase(getAllStamps.rejected, (state, action: IAction<SerializedError>) => {
            state.error = action.error;
            state.loading = false;
        });

        /**
         * Reducers for the 'saveStamp' thunk.
         */
        builder.addCase(saveStamp.pending, (state: IUserStampsState) => {
            state.loading = true;
        });

        builder.addCase(saveStamp.fulfilled, state => {
            state.isStampSaved = true;

            state.loading = false;
            state.error = null;
            GlobalVariables.removeFileById(state.fileInfo.id);
            state.fileInfo = null;
        });
        builder.addCase(
            saveStamp.rejected,
            (state: IUserStampsState, action: IAction<SerializedError>) => {
                state.error = action.error;
            },
        );

        /**
         * Reducers for the 'getStampImage' thunk.
         */
        builder.addCase(getStampImage.pending, (state: IUserStampsState) => {
            state.loading = true;
        });
        builder.addCase(getStampImage.fulfilled, (state: IUserStampsState) => {
            state.loading = false;
        });
        builder.addCase(
            getStampImage.rejected,
            (state: IUserStampsState, action: IAction<SerializedError>) => {
                state.error = action.error;
                state.loading = false;
            },
        );

        /**
         * Reducers for the 'deleteStamp' thunk.
         */
        builder.addCase(deleteStamp.pending, (state: IUserStampsState) => {
            state.loading = false;
        });
        builder.addCase(
            deleteStamp.fulfilled,
            (state: IUserStampsState, action: IAction<string>) => {
                state.loading = false;
                const stampId = action.payload;

                const newStamps = state.stampsResponse.filter(x => x.id !== stampId);

                state.stampsResponse = newStamps;
                state.stamps = mapStamps(newStamps);
                state.categories = mapCategories(newStamps);
                state.error = null;
            },
        );
        builder.addCase(
            deleteStamp.rejected,
            (state: IUserStampsState, action: IAction<SerializedError>) => {
                state.error = action.error;
                state.loading = false;
            },
        );
    },
};

/**
 * Interface that describes the state object for this redux slice.
 */
export interface IUserStampsState extends SlicePropsWithData<IUserStamps>, IFileInfoState {
    isCreateStampDialogOpen: boolean;
    isStampSaved: boolean;
    categories: string[];
    stamps: IUserStamps[];
    stampsResponse: IUserStamp[];
}

/**
 * Interface that describes the settings for a user.
 */
export interface IUserStamps {
    category: string;
    stamps: IUserStamp[];
}

export interface IUserStamp {
    id: string;
    name: string;
    category: string;
    scale: number;
    fileExtension: string;
}

/**
 * Expose thunk functions as exports.
 */
export const getAllStamps = createAsyncThunk(`${urlStampsAll}`, thunks.getStamps);
export const getStampImage = createAsyncThunk(`${urlStampsImage}`, thunks.getStampImage);
export const saveStamp = createAsyncThunk(`${urlStampsSave}`, thunks.saveStamp);
export const deleteStamp = createAsyncThunk(`${urlStampsDelete}`, thunks.deleteStamp);

/**
 * Create the redux slice.
 */
export const userStampSettingsSlice = createSlice({
    name: 'userStampSettings',
    initialState: initialState,
    reducers,
    extraReducers: thunks._reducers,
});

/**
 * Export functions from slice actions.
 */
export const { openCreateStampDialog, closeCreateStampDialog, appendFilesInfo, removeFile } =
    userStampSettingsSlice.actions;

/**
 * Export slice reducer as default.
 */
export default userStampSettingsSlice.reducer;
